import React, { useState, useMemo } from 'react'
import { string, func, array, object, number, bool } from 'prop-types'
import { css } from '@emotion/react'
import {
  Heading,
  Button,
  SpacingToken,
} from '@livechat/design-system-react-components'
import uEmojiParser from 'universal-emoji-parser'

import useHydrationContext from '@/hooks/useHydrationContext'
import { PRODUCT_PAGE_TABS } from '@/constants'
import theme from '@/theme'

import Rating from '@/components/Rating'
import RatingModal from '@/components/Rating/Modal'
import Section from '../Section'
import TextSection from '../TextSection'
import ReviewsSummary from './Summary'

const orTextCss = css`
  margin-left: 8px;
  margin-right: 4px;
`

const reviewTitleCss = css`
  display: flex;
  align-items: center;

  > h3 {
    margin: 0 var(${SpacingToken.Spacing1}) 0 0;
    font-weight: 600;
  }

  > div {
    margin-left: var(${SpacingToken.Spacing1});
  }
`

const listWrapperCss = css`
  > div {
    min-width: 50%;
  }
`

const buttonsWrapperCss = css`
  display: flex;
  align-items: center;
  gap: var(${SpacingToken.Spacing1});
`

const normalizeReviews = (reviews) => {
  if (!reviews) {
    return []
  }

  return reviews
    .map((review) => {
      const { text, name, rate } = review

      if (!text || !name) {
        return null
      }

      const emojiParsedText = uEmojiParser.parseToUnicode(text)

      return {
        title: (
          <div css={reviewTitleCss}>
            <Heading bold as="h3" size="xs">
              {name}
            </Heading>
            <Rating
              rate={rate}
              votes={reviews?.length || 0}
              size={14}
              hideSummary
            />
          </div>
        ),
        description: emojiParsedText,
      }
    })
    .filter(Boolean)
}

const Reviews = ({
  id,
  rate,
  votes,
  reviews,
  histogram,
  canVote,
  developer,
  innerRef,
}) => {
  const hydrated = useHydrationContext()
  const [displayFormOnInit, setDisplayFormOnInit] = useState(false)
  const [isRatingModalOpen, setRatingModalOpen] = useState(false)

  const normalizedReviews = useMemo(() => normalizeReviews(reviews), [reviews])
  const lastReviews = normalizedReviews.slice(0, 4)
  const hasMoreReviews = normalizedReviews?.length > lastReviews?.length

  const openAllReviews = () => setRatingModalOpen(true)

  const openReviewForm = () => {
    setRatingModalOpen(true)
    setDisplayFormOnInit(true)
  }

  const onCloseModal = () => {
    setRatingModalOpen(false)
    setDisplayFormOnInit(false)
  }

  return (
    hydrated && (
      <>
        <Section
          id={PRODUCT_PAGE_TABS.REVIEWS}
          title="Ratings & Reviews"
          ref={innerRef}
        >
          <ReviewsSummary
            id={id}
            rate={rate}
            votes={votes}
            histogram={histogram}
            developer={developer}
          />

          {lastReviews?.length > 0 && (
            <TextSection
              cols={2}
              items={lastReviews}
              color={theme.colors.gray200}
              css={listWrapperCss}
            />
          )}

          <div css={buttonsWrapperCss}>
            {hasMoreReviews && (
              <>
                <Button kind="secondary" onClick={openAllReviews}>
                  Show all {normalizedReviews?.length} reviews
                </Button>
                {canVote && <span css={orTextCss}>or</span>}
              </>
            )}

            {canVote && (
              <Button
                kind={hasMoreReviews ? 'text' : 'secondary'}
                onClick={openReviewForm}
              >
                Leave your own review
              </Button>
            )}
          </div>
        </Section>

        {isRatingModalOpen && (
          <RatingModal
            id={id}
            onClose={onCloseModal}
            displayFormOnInit={displayFormOnInit}
          />
        )}
      </>
    )
  )
}

Reviews.propTypes = {
  id: string,
  reviews: array,
  histogram: object,
  rate: number,
  votes: number,
  canVote: bool,
  developer: object,
  innerRef: func,
}

export default React.forwardRef((props, ref) => (
  <Reviews innerRef={ref} {...props} />
))
